import { FabricObject, Point, LineProps } from "../typedefs";
import type { AligningGuidelines } from "../index";
import { getDistanceList } from "./basic";

export type CollectPointProps = {
  target: FabricObject;
  /** Operation points of the target element: top-left, bottom-left, top-right, bottom-right */
  point: Point;
  /** Position using diagonal points when resizing/scaling. */
  diagonalPoint: Point;
  /** Set of points to consider for alignment: [tl, tr, br, bl, center] */
  list: Point[];
  /** Change the zoom or change the size, determine by whether e.transform.action starts with the string "scale" */
  isScale: boolean;
  /** Whether to change uniformly is determined by canvas.uniformScaling and canvas.uniScaleKey. */
  isUniform: boolean;
  /** When holding the centerKey (default is altKey), the shape will scale based on the center point, with the reference point being the center. */
  isCenter: boolean;
  /** tl、tr、br、bl、mt、mr、mb、ml */
  corner: string;
};

export type PointSnap = {
  axis: "x" | "y";
  arr: Point[];
  dis: number;
  scaleFactor: number;
};

function getPointSnap(
  this: AligningGuidelines,
  props: CollectPointProps,
  axis: PointSnap["axis"]
): PointSnap | null {
  const { target, corner, point, list } = props;
  const { dis, arr } = getDistanceList(point, list, axis);
  const margin = this.margin / this.canvas.getZoom();
  if (dis > margin) return null;

  const width = target.width || 0;
  const height = target.height || 0;
  const scaleX = target.scaleX || 1;
  const scaleY = target.scaleY || 1;

  const dStrokeWidth = target.strokeUniform ? 0 : target.strokeWidth || 0;
  if (axis === "x") {
    let delta = arr[arr.length - 1].x - point.x;
    // tl bl ml
    // If modifying on the left side, the size decreases; conversely, it increases.
    const dirX = corner.includes("l") ? -1 : 1;
    delta *= dirX;

    const scaleWidth = scaleX * width + dStrokeWidth;
    const scaleFactor = (delta + scaleWidth) / scaleWidth;
    if (scaleFactor === 0) return null;
    return { axis, arr, dis, scaleFactor };
  }

  let delta = arr[arr.length - 1].y - point.y;
  // tl mt tr
  // If modifying on the top side, the size decreases; conversely, it increases.
  const dirY = corner.includes("t") ? -1 : 1;
  delta *= dirY;

  const scaleHeight = scaleY * height + dStrokeWidth;
  const scaleFactor = (delta + scaleHeight) / scaleHeight;
  if (scaleFactor === 0) return null;
  return { axis, arr, dis, scaleFactor };
}

export function applyPointSnap(
  this: AligningGuidelines,
  props: CollectPointProps,
  snap: PointSnap
): LineProps[] {
  const { target, isScale, isUniform, corner, point, diagonalPoint, isCenter } =
    props;
  const { axis, arr, scaleFactor } = snap;

  const width = target.width || 0;
  const height = target.height || 0;
  const scaleX = target.scaleX || 1;
  const scaleY = target.scaleY || 1;

  if (axis === "x") {
    if (isScale) {
      target.set("scaleX", scaleX * scaleFactor);
      if (isUniform) target.set("scaleY", scaleY * scaleFactor);
    } else {
      target.set("width", width * scaleFactor);
      if (isUniform) target.set("height", height * scaleFactor);
    }
  } else {
    if (isScale) {
      target.set("scaleY", scaleY * scaleFactor);
      if (isUniform) target.set("scaleX", scaleX * scaleFactor);
    } else {
      target.set("height", height * scaleFactor);
      if (isUniform) target.set("width", width * scaleFactor);
    }
  }
  if (isCenter) {
    target.setPositionByOrigin(diagonalPoint, "center", "center");
  } else {
    const originArr = this.contraryOriginMap;
    target.setPositionByOrigin(diagonalPoint, ...originArr[corner]);
  }
  target.setCoords();
  const currentPoint = this.getPointMap(target)[corner] ?? point;
  return arr.map((targetPoint) => ({
    origin: currentPoint,
    target: targetPoint,
  }));
}

export function getVerticalPointSnap(
  this: AligningGuidelines,
  props: CollectPointProps
): PointSnap | null {
  return getPointSnap.call(this, props, "x");
}

export function getHorizontalPointSnap(
  this: AligningGuidelines,
  props: CollectPointProps
): PointSnap | null {
  return getPointSnap.call(this, props, "y");
}

export function collectVerticalPoint(
  this: AligningGuidelines,
  props: CollectPointProps
): LineProps[] {
  const snap = getVerticalPointSnap.call(this, props);
  return snap ? applyPointSnap.call(this, props, snap) : [];
}

export function collectHorizontalPoint(
  this: AligningGuidelines,
  props: CollectPointProps
): LineProps[] {
  const snap = getHorizontalPointSnap.call(this, props);
  return snap ? applyPointSnap.call(this, props, snap) : [];
}
